import { createEmailSignatureEditor, sanitizeSignatureHtml as sanitizeEmailSignatureHtml } from './email-signature-editor.js'

// Adaptateur du contrat `email-sender` : il sécurise les valeurs reçues du
// backend avant de les donner au renderer et au formulaire DOM.
function normalizeEmailSenderUi(result) {
  if (!result || typeof result !== 'object') return null

  var ui = result.ui || result.emailSenderUi || null
  if (!ui || typeof ui !== 'object') return null
  if (String(ui.type || '').toLowerCase() !== 'email-sender') return null

  var sender = normalizeSender(ui.sender || result.sender)
  if (!sender) return null

  var fields = Array.isArray(ui.fields)
    ? ui.fields.map(normalizeField).filter(Boolean)
    : buildDefaultFields(sender)

  return {
    type: 'email-sender',
    title: String(ui.title || "Validation de l'expéditeur"),
    sender: sender,
    fields: fields,
    status: normalizeStatus(ui.status, sender.signatureConfigured),
    actions: normalizeActions(ui.actions),
    signatureEditor: normalizeSignatureEditor(ui.signatureEditor),
    nextStep: normalizeNextStep(ui.nextStep)
  }
}

function normalizeSender(value) {
  // Une signature absente ne doit pas empêcher la validation de l'expéditeur :
  // on conserve toujours au moins une option éditable côté interface.
  if (!value || typeof value !== 'object') return null
  var signatures = Array.isArray(value.signatures)
    ? value.signatures.map(normalizeSignature).filter(Boolean)
    : []
  if (!signatures.length) {
    signatures = [{ label: 'Signature par défaut', htmlSignature: '', default: true }]
  }
  var selectedSignatureIndex = Number(value.selectedSignatureIndex)
  if (!Number.isFinite(selectedSignatureIndex) || selectedSignatureIndex < 0 || selectedSignatureIndex >= signatures.length) {
    selectedSignatureIndex = findDefaultSignatureIndex(signatures)
  }
  signatures = signatures.map(function (signature, index) {
    return Object.assign({}, signature, { default: index === selectedSignatureIndex })
  })
  var selectedSignature = signatures[selectedSignatureIndex] || null

  return {
    sendingProfileId: String(value.sendingProfileId || '').trim() || null,
    sendingProfileLabel: String(value.sendingProfileLabel || '').trim() || null,
    providerEngineId: String(value.providerEngineId || '').trim() || null,
    firstName: String(value.firstName || '').trim(),
    lastName: String(value.lastName || '').trim(),
    jobTitle: String(value.jobTitle || '').trim(),
    email: String(value.email || '').trim(),
    signatures: signatures,
    selectedSignatureIndex: selectedSignatureIndex,
    signatureConfigured: Boolean(selectedSignature && String(selectedSignature.htmlSignature || '').trim())
  }
}

function normalizeSignature(value) {
  if (!value || typeof value !== 'object') return null
  var label = String(value.label || '').trim()
  var htmlSignature = sanitizeEmailSignatureHtml(value.htmlSignature || value.htmlsignature || '')
  if (!label && !htmlSignature) return null

  return {
    label: label || 'Signature',
    htmlSignature: htmlSignature,
    default: value.default === true
  }
}

function normalizeField(field) {
  if (!field || typeof field !== 'object') return null
  var key = String(field.key || '').trim()
  var label = String(field.label || '').trim()
  var control = String(field.control || '').trim()
  if (!key || !label || ['text', 'email', 'textarea', 'select'].indexOf(control) < 0) return null

  return {
    key: key,
    label: label,
    control: control,
    value: String(field.value || '').trim(),
    options: Array.isArray(field.options)
      ? field.options.map(normalizeOption).filter(Boolean)
      : []
  }
}

function normalizeOption(option) {
  // Les métadonnées du profil sont embarquées dans les options du select pour
  // permettre un changement instantané sans nouvel appel réseau.
  if (!option || typeof option !== 'object') return null
  var value = String(option.value || '').trim()
  var label = String(option.label || '').trim()
  if (!value || !label) return null
  return {
    value: value,
    label: label,
    fromEmail: String(option.fromEmail || '').trim(),
    fromName: String(option.fromName || '').trim(),
    providerEngineId: String(option.providerEngineId || '').trim(),
    jobTitle: String(option.jobTitle || '').trim(),
    signatures: Array.isArray(option.signatures) ? option.signatures.map(normalizeSignature).filter(Boolean) : []
  }
}

function buildDefaultFields(sender) {
  return [
    { key: 'sendingProfileId', label: 'Expéditeur', control: 'select', value: sender.sendingProfileId || '', options: [] },
    { key: 'firstName', label: 'Prénom', control: 'text', value: sender.firstName },
    { key: 'lastName', label: 'Nom', control: 'text', value: sender.lastName },
    { key: 'jobTitle', label: 'Fonction', control: 'text', value: sender.jobTitle },
    { key: 'email', label: 'Email', control: 'email', value: sender.email },
    {
      key: 'signature',
      label: 'Signature',
      control: 'select',
      value: String(sender.selectedSignatureIndex),
      options: sender.signatures.map(function (signature, index) {
        return { value: String(index), label: signature.label || 'Signature' }
      })
    }
  ]
}

function normalizeStatus(status, configured) {
  var fallbackLabel = configured ? 'Configurée' : 'Non configurée'
  return {
    configured: Boolean(status && typeof status === 'object' ? status.configured : configured),
    label: String(status && typeof status === 'object' && status.label || fallbackLabel),
    configuredLabel: String(status && typeof status === 'object' && status.configuredLabel || 'Configurée'),
    notConfiguredLabel: String(status && typeof status === 'object' && status.notConfiguredLabel || 'Non configurée')
  }
}

function normalizeActions(actions) {
  return Object.assign({
    edit: 'Modifier',
    save: 'Enregistrer',
    cancel: 'Annuler',
    configureSignature: 'Configurer la signature',
    confirm: "Générer l'email"
  }, actions && typeof actions === 'object' ? actions : {})
}

function normalizeSignatureEditor(editor) {
  return Object.assign({
    title: 'Signature HTML',
    imageLabel: 'Ajouter une image',
    dropLabel: 'Déposez une image ici',
    htmlLabel: 'HTML de signature',
    apply: 'Appliquer',
    close: 'Fermer'
  }, editor && typeof editor === 'object' ? editor : {})
}

function normalizeNextStep(nextStep) {
  if (!nextStep || typeof nextStep !== 'object') {
    return { key: 'EMailGenerate', label: 'EMailGenerate' }
  }

  return {
    key: String(nextStep.key || 'EMailGenerate'),
    label: String(nextStep.label || nextStep.key || 'EMailGenerate')
  }
}

function renderEmailSenderUi(emailSenderUi) {
  // L'écran présente d'abord un résumé. Le formulaire reste caché jusqu'à
  // l'action Modifier ; les actions de signature et de confirmation restent
  // disponibles au niveau du message.
  return (
    '<div class="wc-email-sender-ui">' +
      '<div class="wc-email-sender-title">' + escapeHtml(emailSenderUi.title) + '</div>' +
      '<div class="wc-email-sender-values">' +
        renderSenderRows(emailSenderUi) +
      '</div>' +
      renderSenderForm(emailSenderUi) +
      renderSignaturePopin(emailSenderUi) +
      '<div class="wc-email-sender-actions">' +
        '<button type="button" class="wc-email-sender-button wc-email-sender-edit">' + escapeHtml(emailSenderUi.actions.edit) + '</button>' +
        '<button type="button" class="wc-email-sender-button wc-email-sender-signature">' + escapeHtml(emailSenderUi.actions.configureSignature) + '</button>' +
        '<button type="button" class="wc-email-sender-button wc-email-sender-confirm">' + escapeHtml(emailSenderUi.actions.confirm) + '</button>' +
      '</div>' +
    '</div>'
  )
}

function renderSenderRows(emailSenderUi) {
  var sender = emailSenderUi.sender
  var rows = [
    { key: 'sendingProfileId', label: findFieldLabel(emailSenderUi, 'sendingProfileId') || 'Expéditeur', value: escapeHtml(sender.sendingProfileLabel || sender.email || '') },
    { key: 'name', label: 'Nom/prénom', value: escapeHtml([sender.firstName, sender.lastName].filter(Boolean).join(' ')) },
    { key: 'jobTitle', label: findFieldLabel(emailSenderUi, 'jobTitle') || 'Fonction', value: escapeHtml(sender.jobTitle) },
    { key: 'email', label: findFieldLabel(emailSenderUi, 'email') || 'Email', value: escapeHtml(sender.email) },
    { key: 'signature', label: findFieldLabel(emailSenderUi, 'signature') || 'Signature', value: renderSignatureStatus(emailSenderUi) }
  ]

  return rows.map(function (item) {
    return (
      '<div class="wc-email-sender-row wc-email-sender-row-' + escapeAttribute(item.key) + '" data-email-sender-row="' + escapeAttributeValue(item.key) + '">' +
        '<div class="wc-email-sender-label">' + escapeHtml(item.label) + '</div>' +
        '<div class="wc-email-sender-value">' + item.value + '</div>' +
      '</div>'
    )
  }).join('')
}

function renderSignatureStatus(emailSenderUi) {
  var statusClass = emailSenderUi.sender.signatureConfigured ? ' wc-email-sender-status-ok' : ''
  var mark = emailSenderUi.sender.signatureConfigured ? '<span class="wc-email-sender-check" aria-hidden="true">✓</span>' : ''
  return '<span class="wc-email-sender-status' + statusClass + '">' + mark + escapeHtml(emailSenderUi.status.label) + '</span>'
}

function renderSenderForm(emailSenderUi) {
  if (!emailSenderUi.fields.length) return ''

  return (
    '<form class="wc-email-sender-form" hidden>' +
      emailSenderUi.fields.map(renderSenderField).join('') +
      '<div class="wc-email-sender-form-actions">' +
        '<button type="submit" class="wc-email-sender-button wc-email-sender-save">' + escapeHtml(emailSenderUi.actions.save) + '</button>' +
        '<button type="button" class="wc-email-sender-button wc-email-sender-cancel">' + escapeHtml(emailSenderUi.actions.cancel) + '</button>' +
      '</div>' +
    '</form>'
  )
}

function renderSenderField(field) {
  var id = 'wc-email-sender-field-' + escapeAttribute(field.key)
  var control = field.control === 'select'
    ? renderSenderSelect(id, field)
    : renderSenderInput(id, field)

  return (
    '<label class="wc-email-sender-field" for="' + id + '">' +
      '<span>' + escapeHtml(field.label) + '</span>' +
      control +
    '</label>'
  )
}

function renderSenderInput(id, field) {
  if (field.control === 'textarea') {
    return '<textarea id="' + id + '" rows="3" data-email-sender-field="' + escapeAttributeValue(field.key) + '">' + escapeHtml(field.value) + '</textarea>'
  }

  return '<input id="' + id + '" type="' + escapeAttributeValue(field.control) + '" value="' + escapeAttributeValue(field.value) + '" data-email-sender-field="' + escapeAttributeValue(field.key) + '">'
}

function renderSenderSelect(id, field) {
  return (
    '<select id="' + id + '" data-email-sender-field="' + escapeAttributeValue(field.key) + '">' +
      field.options.map(function (option) {
        var selected = option.value === field.value ? ' selected' : ''
        return '<option value="' + escapeAttributeValue(option.value) + '"' + selected +
          ' data-from-email="' + escapeAttributeValue(option.fromEmail || '') + '"' +
          ' data-from-name="' + escapeAttributeValue(option.fromName || '') + '"' +
          ' data-provider-engine-id="' + escapeAttributeValue(option.providerEngineId || '') + '"' +
          ' data-job-title="' + escapeAttributeValue(option.jobTitle || '') + '"' +
          ' data-signatures="' + escapeAttributeValue(JSON.stringify(option.signatures || [])) + '"' +
          '>' + escapeHtml(option.label) + '</option>'
      }).join('') +
    '</select>'
  )
}

function renderSignaturePopin(emailSenderUi) {
  var editor = emailSenderUi.signatureEditor
  return (
    '<div class="wc-email-signature-overlay" hidden>' +
      '<div class="wc-email-signature-popin" role="dialog" aria-modal="true">' +
        '<div class="wc-email-signature-head">' +
          '<div class="wc-email-signature-title">' + escapeHtml(editor.title) + '</div>' +
          '<button type="button" class="wc-email-signature-close" aria-label="' + escapeAttributeValue(editor.close) + '">×</button>' +
        '</div>' +
        '<div class="wc-email-signature-editor-host"></div>' +
        '<div class="wc-email-signature-actions">' +
          '<button type="button" class="wc-email-sender-button wc-email-signature-apply">' + escapeHtml(editor.apply) + '</button>' +
        '</div>' +
      '</div>' +
    '</div>'
  )
}

function bindEmailSenderUi(el, emailSenderUi, handlers) {
  // `state.sender` est l'état de travail du message. Il permet de modifier le
  // résumé et la signature sans muter le payload initial reçu du backend.
  if (!el || !emailSenderUi) return
  var state = {
    sender: cloneObject(emailSenderUi.sender)
  }
  var root = el.querySelector('.wc-email-sender-ui')
  var form = el.querySelector('.wc-email-sender-form')
  var editButton = el.querySelector('.wc-email-sender-edit')
  var confirmButton = el.querySelector('.wc-email-sender-confirm')
  var cancelButton = el.querySelector('.wc-email-sender-cancel')
  var signatureButton = el.querySelector('.wc-email-sender-signature')
  var overlay = el.querySelector('.wc-email-signature-overlay')
  var editorHost = el.querySelector('.wc-email-signature-editor-host')
  var closeButton = el.querySelector('.wc-email-signature-close')
  var applyButton = el.querySelector('.wc-email-signature-apply')
  var signatureEditor = null
  var pendingSignatureHtml = ''

  if (editButton && form) {
    editButton.addEventListener('click', function () {
      root && root.classList.add('wc-email-sender-editing')
      form.removeAttribute('hidden')
    })
  }

  if (cancelButton && form) {
    cancelButton.addEventListener('click', function () {
      fillFormFromSender(form, emailSenderUi, state.sender)
      root && root.classList.remove('wc-email-sender-editing')
      form.setAttribute('hidden', 'hidden')
    })
  }

  if (form) {
    var profileControl = form.querySelector('[data-email-sender-field="sendingProfileId"]')
    if (profileControl) {
      profileControl.addEventListener('change', function () {
        // Le profil sélectionné fournit plusieurs champs dépendants. On les
        // applique avant de relire le formulaire, sinon les anciennes valeurs
        // prénom/nom/fonction/signature écrasent le nouveau profil.
        state.sender = applySelectedProfile(profileControl, state.sender)
        updateSignatureFormOptions(form, state.sender)
        fillFormFromSender(form, emailSenderUi, state.sender)
        updateSenderRows(el, emailSenderUi, state.sender)
      })
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault()
      state.sender = applyFormValues(form, state.sender)
      updateSenderRows(el, emailSenderUi, state.sender)
      root && root.classList.remove('wc-email-sender-editing')
      form.setAttribute('hidden', 'hidden')
    })
  }

  if (signatureButton && overlay && editorHost) {
    signatureButton.addEventListener('click', function () {
      pendingSignatureHtml = getSelectedSignature(state.sender).htmlSignature || ''
      if (!signatureEditor) {
        signatureEditor = createEmailSignatureEditor({
          target: editorHost,
          html: pendingSignatureHtml,
          showHeader: false,
          labels: {
            toolbar: emailSenderUi.signatureEditor.title,
            image: emailSenderUi.signatureEditor.imageLabel,
            html: emailSenderUi.signatureEditor.htmlLabel,
            placeholder: emailSenderUi.signatureEditor.htmlLabel,
            htmlHelp: ''
          },
          onChange: function (html) {
            pendingSignatureHtml = html
          }
        })
      } else {
        signatureEditor.setHtml(pendingSignatureHtml)
      }
      overlay.removeAttribute('hidden')
      var editable = editorHost.querySelector('[data-signature-editable]')
      if (editable) editable.focus()
    })
  }

  if (closeButton && overlay) {
    closeButton.addEventListener('click', function () {
      overlay.setAttribute('hidden', 'hidden')
    })
  }

  if (overlay) {
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) overlay.setAttribute('hidden', 'hidden')
    })
  }

  if (applyButton && overlay) {
    applyButton.addEventListener('click', function () {
      pendingSignatureHtml = signatureEditor ? signatureEditor.getHtml() : pendingSignatureHtml
      applySignatureHtml(state.sender, sanitizeEmailSignatureHtml(pendingSignatureHtml))
      updateSenderRows(el, emailSenderUi, state.sender)
      updateSignatureFormOptions(form, state.sender)
      overlay.setAttribute('hidden', 'hidden')
    })
  }

  if (confirmButton) {
    confirmButton.addEventListener('click', function () {
      if (form && !form.hasAttribute('hidden')) {
        state.sender = applyFormValues(form, state.sender)
        updateSenderRows(el, emailSenderUi, state.sender)
      }

      if (handlers && typeof handlers.onConfirm === 'function') {
        handlers.onConfirm({
          label: emailSenderUi.actions.confirm,
          nextStepKey: emailSenderUi.nextStep.key,
          sender: state.sender
        })
      }
    })
  }
}

function fillFormFromSender(form, emailSenderUi, sender) {
  emailSenderUi.fields.forEach(function (field) {
    var control = form.querySelector('[data-email-sender-field="' + escapeSelectorValue(field.key) + '"]')
    if (!control) return
    control.value = field.key === 'signature' ? String(sender.selectedSignatureIndex) : String(sender[field.key] || '')
  })
}

function applyFormValues(form, previousSender) {
  // Utilisé à l'enregistrement, ce parcours conserve les modifications
  // manuelles du formulaire tout en traitant le profil sélectionné comme une
  // source de métadonnées (nom, email, fonction et signatures).
  var sender = cloneObject(previousSender)
  Array.prototype.slice.call(form.querySelectorAll('[data-email-sender-field]')).forEach(function (control) {
    var key = control.getAttribute('data-email-sender-field')
    var value = String(control.value || '').trim()
    if (key === 'signature') {
      sender.selectedSignatureIndex = normalizeSelectedSignatureIndex(value, sender.signatures)
      sender.signatures = sender.signatures.map(function (signature, index) {
        return Object.assign({}, signature, { default: index === sender.selectedSignatureIndex })
      })
    } else if (key && key in sender) {
      sender[key] = value
      if (key === 'sendingProfileId') {
        applyProfileOption(sender, control)
      }
    }
  })

  refreshSignatureConfigured(sender)
  return sender
}

function applySelectedProfile(control, previousSender) {
  var sender = cloneObject(previousSender)
  applyProfileOption(sender, control)
  refreshSignatureConfigured(sender)
  return sender
}

function applyProfileOption(sender, control) {
  // Les attributs data-* sont produits par le backend dans chaque option du
  // select afin que le changement d'expéditeur soit entièrement déterministe
  // et immédiat côté client.
  var option = control && control.selectedOptions && control.selectedOptions[0]
  if (!option) return

  sender.sendingProfileId = String(control.value || '').trim()
  sender.sendingProfileLabel = String(option.textContent || '').trim()
  sender.providerEngineId = String(option.getAttribute('data-provider-engine-id') || '').trim() || sender.providerEngineId
  sender.email = String(option.getAttribute('data-from-email') || '').trim() || sender.email
  sender.jobTitle = String(option.getAttribute('data-job-title') || '').trim()

  try {
    var signatures = JSON.parse(option.getAttribute('data-signatures') || '[]')
    if (Array.isArray(signatures) && signatures.length) {
      sender.signatures = signatures
      sender.selectedSignatureIndex = normalizeSelectedSignatureIndex(
        signatures.findIndex(function (signature) { return signature.default === true }),
        signatures
      )
    }
  } catch (error) {
    // Keep current signatures when optional profile metadata is invalid.
  }

  var fromName = String(option.getAttribute('data-from-name') || '').trim()
  if (fromName) {
    var parts = fromName.split(/\s+/).filter(Boolean)
    sender.firstName = parts.shift() || sender.firstName
    sender.lastName = parts.join(' ') || sender.lastName
  }
}

function applySignatureHtml(sender, htmlSignature) {
  var index = normalizeSelectedSignatureIndex(sender.selectedSignatureIndex, sender.signatures)
  sender.signatures[index] = Object.assign({}, sender.signatures[index], {
    htmlSignature: htmlSignature,
    default: true
  })
  sender.signatures = sender.signatures.map(function (signature, signatureIndex) {
    return Object.assign({}, signature, { default: signatureIndex === index })
  })
  sender.selectedSignatureIndex = index
  refreshSignatureConfigured(sender)
}

function updateSenderRows(el, emailSenderUi, sender) {
  var rows = {
    name: escapeHtml([sender.firstName, sender.lastName].filter(Boolean).join(' ')),
    sendingProfileId: escapeHtml(sender.sendingProfileLabel || sender.email || ''),
    jobTitle: escapeHtml(sender.jobTitle),
    email: escapeHtml(sender.email),
    signature: renderSignatureStatus(Object.assign({}, emailSenderUi, {
      sender: sender,
      status: {
        configured: sender.signatureConfigured,
        label: sender.signatureConfigured ? emailSenderUi.status.configuredLabel : emailSenderUi.status.notConfiguredLabel
      }
    }))
  }

  Object.keys(rows).forEach(function (key) {
    var row = el.querySelector('[data-email-sender-row="' + escapeSelectorValue(key) + '"] .wc-email-sender-value')
    if (row) row.innerHTML = rows[key]
  })
}

function updateSignatureFormOptions(form, sender) {
  if (!form) return
  var select = form.querySelector('[data-email-sender-field="signature"]')
  if (!select) return
  select.innerHTML = sender.signatures.map(function (signature, index) {
    var selected = index === sender.selectedSignatureIndex ? ' selected' : ''
    return '<option value="' + index + '"' + selected + '>' + escapeHtml(signature.label || 'Signature') + '</option>'
  }).join('')
}

function getSelectedSignature(sender) {
  return sender.signatures[normalizeSelectedSignatureIndex(sender.selectedSignatureIndex, sender.signatures)] || sender.signatures[0]
}

function refreshSignatureConfigured(sender) {
  // Le statut affiché dépend toujours de la signature actuellement
  // sélectionnée, jamais du statut du profil précédent.
  var selectedSignature = getSelectedSignature(sender)
  sender.signatureConfigured = Boolean(selectedSignature && String(selectedSignature.htmlSignature || '').trim())
}

function normalizeSelectedSignatureIndex(value, signatures) {
  var index = Number(value)
  if (!Number.isInteger(index) || index < 0 || index >= signatures.length) return findDefaultSignatureIndex(signatures)
  return index
}

function findDefaultSignatureIndex(signatures) {
  var defaultIndex = signatures.findIndex(function (signature) { return signature.default })
  return defaultIndex >= 0 ? defaultIndex : 0
}

function findFieldLabel(emailSenderUi, key) {
  var field = emailSenderUi.fields.find(function (candidate) { return candidate.key === key })
  return field ? field.label : ''
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function escapeAttribute(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function escapeAttributeValue(value) {
  return escapeHtml(value)
}

function escapeSelectorValue(value) {
  if (typeof CSS !== 'undefined' && CSS.escape) {
    return CSS.escape(String(value || ''))
  }

  return String(value || '').replace(/"/g, '\\"')
}

function cloneObject(value) {
  if (!value || typeof value !== 'object') return null
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (error) {
    return null
  }
}

export { normalizeEmailSenderUi, renderEmailSenderUi, bindEmailSenderUi }
